// frontend/web/src/pages/products/ProductDetailPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Grid,
  Paper, Divider, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Avatar, Card, CardMedia, Accordion, AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Inventory2 as InventoryIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import productService from '../../services/productService';
import ListingProductSidebar from '../../components/common/ListingProductSidebar';
import ListingMap from '../../components/listing/ListingMap';
import ListingDetailsView from '../../components/listing/ListingDetailsView';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';
import { getProductConditionTranslationKey } from '../../constants/productConditions';

const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${UPLOADS_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
};

const STATUS_CONFIG = {
  draft:    { color: 'default',  variant: 'outlined' },
  pending:  { color: 'warning',  variant: 'filled' },
  active:   { color: 'success',  variant: 'filled' },
  paused:   { color: 'warning',  variant: 'outlined' },
  rejected: { color: 'error',    variant: 'filled' },
  archived: { color: 'default',  variant: 'outlined' }
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const { user } = useAuth();
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mapPosition, setMapPosition] = useState([48.8566, 2.3522]);
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    productService.getById(id)
      .then((res) => {
        if (!cancelled) {
          const prod = res.data || res;
          setProduct(prod);
          if (prod.pickup_location) {
            setMapPosition([prod.pickup_location.lat, prod.pickup_location.lng]);
          }
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('detail.notFound'));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [id, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }} color="text.secondary">{t('detail.loading')}</Typography>
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || t('detail.notFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/my-products')}>
          {t('detail.backToProducts')}
        </Button>
      </Box>
    );
  }

  const statusConf = STATUS_CONFIG[product.status] || STATUS_CONFIG.draft;
  const isOwner = user?.id === product.seller_id || user?.id === product.user_id;
  const priceFormatted = product.price_cents
    ? `${(product.price_cents / 100).toFixed(2)} ${product.currency || 'EUR'}`
    : '—';

  const images = (product.images || []).map(getImageUrl).filter(Boolean);
  const mainImage = images[selectedImage] || null;
  const weekDayKeys = ['weekDay.monday', 'weekDay.tuesday', 'weekDay.wednesday', 'weekDay.thursday', 'weekDay.friday', 'weekDay.saturday', 'weekDay.sunday'];
  const conditionLabel = product.condition
    ? t(getProductConditionTranslationKey(product.condition))
    : t('detail.conditionMissing');
  const getVariantTypeLabel = (type) => t(`variantType.${type}`, { defaultValue: type });

  const formatTime = (mins) => {
    if (!mins && mins !== 0) return '—';
    if (mins === 0) return t('prepTimes.0');
    if (mins < 60) return `${mins} ${t('detail.minutes')}`;
    if (mins < 1440) return `${(mins / 60).toFixed(1)}h`;
    return `${(mins / 1440).toFixed(1)}j`;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>

      {/* ── Header gradient ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/my-products')}
            sx={{ color: 'rgba(255,255,255,0.85)', mb: 1, '&:hover': { color: 'white' } }}
          >
            {t('detail.backToProducts')}
          </Button>
          <Typography variant="h4" fontWeight={700}>
            {product.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={t(`status.${product.status}`, product.status)}
              color={statusConf.color}
              variant={statusConf.variant}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label={priceFormatted}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }}
            />
            <Chip
              label={`${t('conditions.label')}: ${conditionLabel}`}
              variant="outlined"
              sx={{ borderColor: 'rgba(255,255,255,0.45)', color: 'white', fontWeight: 700 }}
            />
          </Box>
        </Box>
        {isOwner && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/edit-product/${product.id}`)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', borderColor: 'white' }
            }}
          >
            {t('detail.editProduct')}
          </Button>
        )}
      </Box>

      {/* ── Alertes statut ──────────────────────────────────────────── */}
      {product.status === 'rejected' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('detail.rejectedAlert')}
          {product.rejection_reason && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>{t('form.rejectionReason')} :</strong> {product.rejection_reason}
            </Typography>
          )}
        </Alert>
      )}
      {product.status === 'pending' && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('detail.pendingAlert')}</Alert>
      )}

      <Grid container spacing={3}>

        {/* ── Colonne gauche : images ──────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {mainImage ? (
              <CardMedia
                component="img"
                image={mainImage}
                alt={product.title}
                sx={{ height: 320, objectFit: 'contain', backgroundColor: '#f5f5f5', width: '100%' }}
              />
            ) : (
              <Box
                sx={{
                  height: 320,
                  background: 'linear-gradient(135deg, #f0f2ff 0%, #e8eaff 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <InventoryIcon sx={{ fontSize: 72, color: 'primary.light', opacity: 0.4 }} />
                <Typography variant="body2" color="text.secondary">{t('detail.noImage')}</Typography>
              </Box>
            )}
          </Paper>

          {/* Galerie miniatures */}
          {images.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {images.map((src, i) => (
                <Box
                  key={i}
                  component="img"
                  src={src}
                  alt={`img-${i}`}
                  onClick={() => setSelectedImage(i)}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: i === selectedImage ? '2px solid #6200ee' : '2px solid transparent',
                    opacity: i === selectedImage ? 1 : 0.65,
                    transition: 'all 0.2s',
                    '&:hover': { opacity: 1, borderColor: '#03dac4' }
                  }}
                />
              ))}
            </Box>
          )}
        </Grid>

        {/* ── Colonne droite : détails principal ────────────────────── */}
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>

            {/* Description */}
            {product.description && (
              <>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {product.description}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {/* Catégorie complète */}
            {product.category && (
              <Accordion
                sx={{
                  mb: 2,
                  boxShadow: 3,
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    color: 'white',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center'
                    },
                    '&:hover': {
                      background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    🏷️ {t('detail.category')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Chip
                    label={getCategoryName(product.category) || t('detail.noCategory')}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontSize: '1rem',
                      height: 'auto',
                      py: 1,
                      px: 2,
                      borderRadius: 2,
                      '& .MuiChip-label': {
                        fontWeight: 'medium'
                      }
                    }}
                  />
                  {product.category?.description && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      {getCategoryDescription(product.category)}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {product.details && Object.keys(product.details).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <ListingDetailsView
                  listing={{ details: product.details, offer_type: 'product' }}
                  expanded={detailsExpanded}
                  onChange={(_, isExpanded) => setDetailsExpanded(isExpanded)}
                />
              </Box>
            )}

            {/* Infos grid */}
            <Grid container spacing={2}>

              {/* Prix */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('detail.price')}</Typography>
                <Typography variant="h6" fontWeight={700} color="primary">
                  {priceFormatted}
                </Typography>
              </Grid>

              {/* Quantité totale */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('detail.totalQuantity')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={600}>{product.quantity}</Typography>
                </Box>
              </Grid>

              {/* Devise */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('detail.currency')}</Typography>
                <Typography variant="body2" fontWeight={500}>{product.currency || 'EUR'}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('conditions.label')}</Typography>
                <Typography variant="body2" fontWeight={500}>{conditionLabel}</Typography>
              </Grid>

              {/* Temps de préparation */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('detail.preparationTime')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {formatTime(product.preparation_time_minutes ?? product.preparationTimeMinutes)}
                  </Typography>
                </Box>
              </Grid>

              {/* Durée créneau */}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">{t('detail.slotDuration')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {formatTime(product.default_slot_duration_minutes ?? product.defaultSlotDurationMinutes)}
                  </Typography>
                </Box>
              </Grid>

              {/* Vendeur */}
              {product.seller && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">{t('detail.owner')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Avatar
                      src={product.seller.avatar}
                      sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {product.seller.name}
                      </Typography>
                      {product.seller.email && (
                        <Typography variant="caption" color="text.secondary">
                          {product.seller.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* ── Géolocalisation avec Map ────────────────────────────── */}
        {product.pickup_location && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOnIcon color="error" />
                <Typography variant="h6" fontWeight={600}>{t('detail.pickupAddress')}</Typography>
              </Box>
              <ListingMap
                initialPosition={mapPosition}
                height={350}
              />
              {product.pickup_location.address && (
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  {product.pickup_location.address}
                </Typography>
              )}
            </Paper>
          </Grid>
        )}

        {/* ── Variantes produit ────────────────────────────────────── */}
        {product.variants && product.variants.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InventoryIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>{t('detail.productVariants')}</Typography>
                <Chip size="small" label={product.variants.length} color="primary" />
              </Box>
              
              {product.variants.map((variant, vIdx) => (
                <Accordion key={vIdx} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white' }}>
                    <Typography fontWeight={700}>{getVariantTypeLabel(variant.type)}</Typography>
                    <Chip
                      label={t('detail.variantOptionsCount', { count: variant.options?.length || 0 })}
                      size="small"
                      sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    {variant.options && variant.options.length > 0 ? (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.100' } }}>
                              <TableCell>{t('form.tableHeaderValue')}</TableCell>
                              <TableCell align="right">{t('form.tableHeaderPrice')}</TableCell>
                              <TableCell align="right">{t('form.tableHeaderQty')}</TableCell>
                              <TableCell align="right">{t('form.tableHeaderWeight')}</TableCell>
                              <TableCell align="right">{t('detail.dimensions')}</TableCell>
                              <TableCell>{t('form.tableHeaderSku')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {variant.options.map((opt, oIdx) => {
                              const optPrice = opt.price !== undefined && opt.price !== '' 
                                ? Number(opt.price).toFixed(2)
                                : opt.price_cents !== undefined 
                                  ? (opt.price_cents / 100).toFixed(2)
                                  : '—';
                              return (
                                <TableRow key={oIdx} hover>
                                  <TableCell>{opt.value}</TableCell>
                                  <TableCell align="right" fontWeight={700} sx={{ color: 'primary.main' }}>
                                    {optPrice} {product.currency || 'EUR'}
                                  </TableCell>
                                  <TableCell align="right">{opt.quantity || '—'}</TableCell>
                                  <TableCell align="right">{opt.weight_g || '—'}</TableCell>
                                  <TableCell align="right">
                                    {opt.length_cm || '—'} × {opt.width_cm || '—'} × {opt.height_cm || '—'}
                                  </TableCell>
                                  <TableCell>{opt.sku || '—'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{t('detail.noOption')}</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          </Grid>
        )}

        {/* ── Options de livraison ────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {t('detail.deliveryOptions', 'Options de livraison')}
              </Typography>
              {product.meta?.delivery_enabled ? (
                <Chip
                  size="small"
                  label={t('detail.deliveryAvailable', 'Disponible')}
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              ) : (
                <Chip
                  size="small"
                  label={t('detail.deliveryNotAvailable', 'Non disponible')}
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>

            {product.meta?.delivery_enabled && product.meta?.delivery_config ? (
              <Box>
                <Alert severity="info" icon="🚚" sx={{ mb: 2 }}>
                  {t('detail.deliveryEnabledMessage', 'Le vendeur propose la livraison pour ce produit')}
                </Alert>

                <Grid container spacing={2}>
                  {/* Livraison Standard */}
                  {(product.meta.delivery_config.standard || product.meta.delivery_config.standard_price_cents !== undefined) && (
                    <Grid item xs={12} md={product.meta.delivery_config.express || product.meta.delivery_config.express_price_cents !== undefined ? 6 : 12}>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)',
                          height: '100%'
                        }}
                      >
                        <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                          📦 {t('detail.standardDelivery', 'Livraison Standard')}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          {/* Affichage de la grille tarifaire (nouveau système) */}
                          {product.meta.delivery_config.standard?.pricing_tiers ? (
                            <>
                              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                                {t('detail.deliveryPricingTiers', 'Grille tarifaire')}
                              </Typography>
                              <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                      {t('detail.tierQuantityHeader', 'Quantité')}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                      {t('detail.tierPriceHeader', 'Prix de livraison')}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {product.meta.delivery_config.standard.pricing_tiers.map((tier, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td': { border: 0 } }}>
                                      <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {tier.min_quantity} {tier.max_quantity ? `- ${tier.max_quantity}` : `${t('detail.tierAndMore', 'et plus')}`} {t('detail.tierItems', 'article(s)')}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'primary.main' }}>
                                        {tier.price_cents === 0 
                                          ? t('detail.freeDelivery', 'GRATUIT')
                                          : `${(tier.price_cents / 100).toFixed(2)} ${product.currency || 'EUR'}`
                                        }
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              
                              <Divider sx={{ my: 1.5 }} />
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.estimatedDays', 'Délai estimé')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {product.meta.delivery_config.standard.days || 3} {t('detail.days', 'jours')}
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            /* Ancien système avec prix fixe (rétrocompatibilité) */
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.deliveryPrice', 'Prix')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={700} color="primary.main">
                                  {product.meta.delivery_config.standard_price_cents === 0 
                                    ? t('detail.freeDelivery', 'GRATUIT')
                                    : `${(product.meta.delivery_config.standard_price_cents / 100).toFixed(2)} ${product.currency || 'EUR'}`
                                  }
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.estimatedDays', 'Délai estimé')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {product.meta.delivery_config.standard_days || 3} {t('detail.days', 'jours')}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  )}

                  {/* Livraison Express (si disponible) */}
                  {(product.meta.delivery_config.express || product.meta.delivery_config.express_price_cents !== undefined) && (
                    <Grid item xs={12} md={6}>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: 'warning.main',
                          borderWidth: 2,
                          background: 'linear-gradient(135deg, #fff9e6 0%, #ffe8cc 100%)',
                          height: '100%'
                        }}
                      >
                        <Typography variant="h6" fontWeight={700} color="warning.dark" gutterBottom>
                          ⚡ {t('detail.expressDelivery', 'Livraison Express')}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          {/* Affichage de la grille tarifaire (nouveau système) */}
                          {product.meta.delivery_config.express?.pricing_tiers ? (
                            <>
                              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                                {t('detail.deliveryPricingTiers', 'Grille tarifaire')}
                              </Typography>
                              <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                      {t('detail.tierQuantityHeader', 'Quantité')}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                      {t('detail.tierPriceHeader', 'Prix de livraison')}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {product.meta.delivery_config.express.pricing_tiers.map((tier, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td': { border: 0 } }}>
                                      <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {tier.min_quantity} {tier.max_quantity ? `- ${tier.max_quantity}` : `${t('detail.tierAndMore', 'et plus')}`} {t('detail.tierItems', 'article(s)')}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'warning.dark' }}>
                                        {tier.price_cents === 0 
                                          ? t('detail.freeDelivery', 'GRATUIT')
                                          : `${(tier.price_cents / 100).toFixed(2)} ${product.currency || 'EUR'}`
                                        }
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              
                              <Divider sx={{ my: 1.5 }} />
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.estimatedDays', 'Délai estimé')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {product.meta.delivery_config.express.days || 1} {t('detail.days', 'jours')}
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            /* Ancien système avec prix fixe (rétrocompatibilité) */
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.deliveryPrice', 'Prix')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={700} color="warning.dark">
                                  {(product.meta.delivery_config.express_price_cents / 100).toFixed(2)} {product.currency || 'EUR'}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('detail.estimatedDays', 'Délai estimé')} :
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {product.meta.delivery_config.express_days || 1} {t('detail.days', 'jours')}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                {/* Note additionnelle */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    ℹ️ {t('detail.deliveryNote', 'Les délais de livraison sont estimatifs et peuvent varier selon votre localisation. Le prix de livraison sera ajouté au total de votre commande.')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  background: 'linear-gradient(145deg, #f8f9ff 0%, #f0f2ff 100%)',
                  borderRadius: 2,
                  border: '1px dashed #e0e7ff'
                }}
              >
                <LocationOnIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  {t('detail.pickupOnlyMessage', 'Ce produit est disponible uniquement en retrait sur place')}
                </Typography>
                {product.pickup_location?.address && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    📍 {product.pickup_location.address}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Horaires d'ouverture hebdomadaires ────────────────── */}
        {product.opening_hours && product.opening_hours.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>{t('detail.weeklyOpeningHours')}</Typography>
              </Box>
              
              <Grid container spacing={2}>
                {weekDayKeys.map((dayKey, dayNum) => {
                  const dayHours = product.opening_hours.find(h => h.day === (dayNum + 1));
                  return (
                    <Grid item xs={12} sm={6} md={4} key={dayNum}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, background: dayHours ? 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)' : '#f5f5f5', borderColor: dayHours ? 'primary.light' : 'divider' }}>
                        <Typography variant="subtitle2" fontWeight={700}>{t(dayKey)}</Typography>
                        {dayHours ? (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                            {dayHours.from} → {dayHours.to}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                            {t('detail.closed')}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* ── Périodes d'indisponibilité ────────────────────────── */}
        {product.unavailabilityPeriods && product.unavailabilityPeriods.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ScheduleIcon color="warning" />
                <Typography variant="h6" fontWeight={600}>{t('detail.unavailabilityPeriods')}</Typography>
                <Chip size="small" label={product.unavailabilityPeriods.length} color="warning" />
              </Box>
              
              <Grid container spacing={2}>
                {product.unavailabilityPeriods.map((period, pIdx) => (
                  <Grid item xs={12} sm={6} md={4} key={pIdx}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'warning.main', borderWidth: 1.5, background: 'linear-gradient(135deg, #fff9e6 0%, #fff3e0 100%)' }}>
                      <Typography variant="body2" fontWeight={600}>
                        📅 {period.available_from}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                        → {period.available_to}
                      </Typography>
                      {period.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          <strong>{t('detail.reasonLabel')}:</strong> {period.reason}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* ── Créneaux de disponibilité ────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ScheduleIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>{t('detail.availabilitySlots')}</Typography>
              {product.availability_slots?.length > 0 && (
                <Chip
                  size="small"
                  label={product.availability_slots.length}
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>

            {(!product.availability_slots || product.availability_slots.length === 0) ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  background: 'linear-gradient(145deg, #f8f9ff 0%, #f0f2ff 100%)',
                  borderRadius: 2,
                  border: '1px dashed #e0e7ff'
                }}
              >
                <ScheduleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">{t('detail.noSlots')}</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                    <TableCell>{t('detail.date')}</TableCell>
                    <TableCell>{t('detail.start')}</TableCell>
                    <TableCell>{t('detail.end')}</TableCell>
                    <TableCell>{t('detail.slotDuration')}</TableCell>
                    <TableCell>{t('detail.capacity')}</TableCell>
                    <TableCell>{t('detail.booked')}</TableCell>
                    <TableCell>{t('detail.slotStatus')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product.availability_slots.map((slot, i) => (
                    <TableRow
                      key={slot.id || i}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>{slot.date}</TableCell>
                      <TableCell>{slot.start_time?.substring(0, 5)}</TableCell>
                      <TableCell>{slot.end_time?.substring(0, 5)}</TableCell>
                      <TableCell>{slot.slot_duration_minutes} {t('detail.minutes')}</TableCell>
                      <TableCell>{slot.capacity}</TableCell>
                      <TableCell>{slot.booked_count || 0}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`status.${slot.status}`, slot.status || 'active')}
                          color={
                            slot.status === 'full' ? 'error'
                            : slot.status === 'paused' ? 'warning'
                            : 'success'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Sidebar */}
      <ListingProductSidebar activePage="products" user={user} />
    </Box>
  );
}
